/**
 * ContentSuggestion — Interactive card for AI-suggested title/description/bullet improvements.
 *
 * Shows the current and suggested values for each field (title, description, bullets).
 * Each suggestion includes an AI-generated rationale explaining why.
 * Users can select which suggestions to apply via checkboxes, then push updates
 * to Amazon via the SP-API PATCH endpoint.
 *
 * Features:
 * - Skeleton loading when AI is still generating suggestions
 * - Expand/collapse for long content (click to see full text)
 * - Rationale display under each suggestion
 * - Bullet points rendered as a numbered list
 * - Checkbox selection + apply button
 */

import { useState, useRef, useEffect } from 'react'
import type { ContentSuggestionBlock } from '../../../stores/CoPilotStore'

const API_BASE = import.meta.env.VITE_API_BASE || ''

type Status = 'idle' | 'loading' | 'success' | 'error'

export function ContentSuggestion({ block }: { block: ContentSuggestionBlock }) {
  const { asin, sku, productTitle, suggestions } = block

  // Track whether the block has been updated at least once (enrichment attempted)
  const prevBlockRef = useRef(block)
  const [enrichmentDone, setEnrichmentDone] = useState(false)

  useEffect(() => {
    if (block !== prevBlockRef.current) {
      prevBlockRef.current = block
      setEnrichmentDone(true)
    }
  }, [block])

  // Show skeleton only while AI is still generating AND enrichment hasn't been attempted yet
  const allEmpty = suggestions.every((s) => !s.suggested)
  const isGenerating = allEmpty && !enrichmentDone

  // Only show suggestions that have AI-generated content
  const validSuggestions = suggestions.filter((s) => s.suggested && s.suggested !== s.current)

  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(validSuggestions.map((_, i) => i))
  )
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const totalToApply = [...selected].length

  // Re-sync selected set when validSuggestions changes (AI enrichment arrives)
  const prevCount = selected.size
  if (validSuggestions.length > 0 && prevCount === 0 && status === 'idle') {
    const newSet = new Set(validSuggestions.map((_, i) => i))
    if (newSet.size > 0) setSelected(newSet)
  }

  function toggleItem(idx: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  function toggleExpand(field: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  async function handleApply() {
    if (totalToApply === 0) return
    setStatus('loading')
    setErrorMsg('')

    try {
      const selectedItems = validSuggestions.filter((_, i) => selected.has(i))

      // Apply each selected suggestion sequentially
      for (const item of selectedItems) {
        const res = await fetch(
          `${API_BASE}/amazon/listings/${encodeURIComponent(sku)}/content`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attribute: item.field,
              value: item.suggested,
            }),
          }
        )

        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error((data as any).error || `Failed to update ${item.label} (${res.status})`)
        }
      }

      setStatus('success')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong')
      setStatus('error')
    }
  }

  function handleRetry() {
    setStatus('idle')
    setErrorMsg('')
  }

  /** Render bullet points as a numbered list */
  function renderBulletPoints(text: string) {
    const bullets = text.split('\n').filter((b) => b.trim())
    return (
      <ol className="list-decimal list-inside space-y-0.5">
        {bullets.map((bullet, i) => (
          <li key={i} className="text-[12px] text-[#3F3F46] leading-snug">
            {bullet}
          </li>
        ))}
      </ol>
    )
  }

  /** Render current bullet points (muted) */
  function renderCurrentBullets(text: string) {
    const bullets = text.split('\n').filter((b) => b.trim())
    if (bullets.length === 0) return null
    return (
      <ol className="list-decimal list-inside space-y-0.5">
        {bullets.map((bullet, i) => (
          <li key={i} className="text-[11px] text-[#A1A1AA] leading-snug">
            {bullet}
          </li>
        ))}
      </ol>
    )
  }

  return (
    <div className="border border-[#E4E4E7] rounded-xl bg-white overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-[#F4F4F5] flex items-center gap-2">
        <ContentIcon />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-[#18181B] truncate">
            {productTitle}
          </div>
        </div>
        <span className="text-[10px] font-mono text-[#A1A1AA] bg-[#F4F4F5] px-1.5 py-0.5 rounded shrink-0">
          {asin}
        </span>
      </div>

      <div className="px-3 py-2.5 space-y-3 flex-1 flex flex-col">
        {/* Skeleton loading state */}
        {isGenerating && (
          <div className="space-y-3 animate-pulse">
            {suggestions.map((s) => (
              <div key={s.field} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="size-3.5 rounded bg-[#E4E4E7]" />
                  <div className="h-3 w-16 bg-[#E4E4E7] rounded" />
                </div>
                <div className="ml-5.5 pl-0.5 space-y-1">
                  <div className="h-3 w-full bg-[#F4F4F5] rounded" />
                  <div className="h-3 w-3/4 bg-[#F4F4F5] rounded" />
                  <div className="h-3 w-1/2 bg-[#F4F4F5] rounded" />
                  {/* Rationale skeleton */}
                  <div className="h-2.5 w-5/6 bg-[#F9F5FF] rounded mt-1" />
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <div className="h-7 w-24 bg-[#F4F4F5] rounded-lg" />
            </div>
          </div>
        )}

        {/* Suggestion list */}
        {!isGenerating && status !== 'success' &&
          validSuggestions.map((item, i) => {
            const isSelected = selected.has(i)
            const isExpanded = expanded.has(item.field)
            const isBullets = item.field === 'bullet_point'
            // Check if text is likely truncated
            const isLong = isBullets
              ? item.suggested.split('\n').length > 3
              : item.suggested.length > 120
            const isCurrentLong = item.current && (isBullets
              ? item.current.split('\n').length > 2
              : item.current.length > 80)

            return (
              <div key={item.field} className="space-y-1">
                {/* Field header with checkbox */}
                <label
                  className={`
                    flex items-center gap-2 cursor-pointer
                    ${status === 'loading' ? 'opacity-50 pointer-events-none' : ''}
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleItem(i)}
                    disabled={status === 'loading'}
                    className="size-3.5 rounded border-[#D4D4D8] text-[#8B5CF6] accent-[#8B5CF6] cursor-pointer"
                  />
                  <span className="text-[12px] font-semibold text-[#52525B] uppercase tracking-wide">
                    {item.label}
                  </span>
                </label>

                {/* Suggested value */}
                <div className="ml-5.5 pl-0.5">
                  {isBullets ? (
                    <div className={!isExpanded && isLong ? 'max-h-[4.5rem] overflow-hidden' : ''}>
                      {renderBulletPoints(item.suggested)}
                    </div>
                  ) : (
                    <div
                      className={`text-[12px] text-[#3F3F46] leading-snug ${!isExpanded && isLong ? 'line-clamp-3' : ''}`}
                    >
                      {item.suggested}
                    </div>
                  )}

                  {/* Rationale */}
                  {item.rationale && (
                    <div className="flex items-start gap-1 mt-1 px-2 py-1 bg-[#F9F5FF] rounded-md">
                      <RationaleIcon />
                      <span className="text-[11px] text-[#7C3AED] leading-snug">
                        {item.rationale}
                      </span>
                    </div>
                  )}

                  {/* Current value (muted) */}
                  {item.current && (
                    <div className="mt-1">
                      {isBullets ? (
                        <div className={!isExpanded && isCurrentLong ? 'max-h-[2.5rem] overflow-hidden' : ''}>
                          <div className="text-[11px] text-[#A1A1AA] mb-0.5">Current:</div>
                          {renderCurrentBullets(item.current)}
                        </div>
                      ) : (
                        <div
                          className={`text-[11px] text-[#A1A1AA] ${!isExpanded && isCurrentLong ? 'line-clamp-1' : ''}`}
                        >
                          Current: {item.current}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Expand/collapse toggle */}
                  {(isLong || isCurrentLong) && (
                    <button
                      onClick={() => toggleExpand(item.field)}
                      className="text-[11px] text-[#8B5CF6] hover:text-[#7C3AED] mt-0.5 cursor-pointer"
                    >
                      {isExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}

        {/* Empty state when all suggestions match current */}
        {!isGenerating && validSuggestions.length === 0 && (
          <div className="flex items-center gap-1.5 py-2 text-[12px] text-[#A1A1AA]">
            <svg className="size-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            Content looks good — no changes suggested
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action area */}
        {!isGenerating && status === 'idle' && validSuggestions.length > 0 && (
          <div className="flex justify-end">
            <button
              onClick={handleApply}
              disabled={totalToApply === 0}
              className={`
                py-1.5 px-3 rounded-lg text-[12px] font-semibold transition-colors
                ${
                  totalToApply > 0
                    ? 'bg-[#8B5CF6] text-white hover:bg-[#7C3AED] cursor-pointer'
                    : 'bg-[#F4F4F5] text-[#A1A1AA] cursor-not-allowed'
                }
              `}
            >
              {totalToApply > 0
                ? `Apply ${totalToApply} update${totalToApply > 1 ? 's' : ''}`
                : 'Select suggestions'}
            </button>
          </div>
        )}

        {status === 'loading' && (
          <div className="flex items-center justify-center gap-1.5 py-1.5 text-[12px] text-[#8B5CF6]">
            <LoadingSpinner />
            Updating...
          </div>
        )}

        {status === 'success' && (
          <div className="flex items-center gap-1.5 py-1.5 px-2.5 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0] text-[12px] text-[#166534]">
            <svg className="size-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            Submitted
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 py-1.5 px-2.5 rounded-lg bg-[#FEF2F2] border border-[#FECACA] text-[11px] text-[#991B1B]">
              <svg className="size-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              {errorMsg || 'Failed'}
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleRetry}
                className="py-1 px-2.5 rounded-lg text-[11px] font-semibold bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7] cursor-pointer transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ContentIcon() {
  return (
    <svg
      className="size-4 text-[#8B5CF6]"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
      />
    </svg>
  )
}

function RationaleIcon() {
  return (
    <svg
      className="size-3 text-[#8B5CF6] mt-0.5 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
      />
    </svg>
  )
}

function LoadingSpinner() {
  return (
    <svg className="size-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}
